<template>
    <article class="container">

        <nav class="row pb-5">
            <label for="search" class="sr-only"></label>
            <input
                    id="search"
                    type="text"
                    tabindex="1"
                    class="form-control form-control-lg"
                    v-model="search"
                    @input="onChange"
                    :placeholder="this.$t('chemicalNameOrPartNumber')"
            >
        </nav>

        <nav class="row">
            <div class="col custom-control custom-switch d-flex justify-content-end">
                <input type="checkbox"
                       class="custom-control-input"
                       id="myChemicalsToggle"
                       tabindex="2"
                       :disabled="!authorized"
                       v-model="clientChemicalsOnly">
                <label id="myChemicalsToggleLabel"
                       data-toggle="tooltip"
                       data-placement="top"
                       :title="myChemicalsCheckboxTooltip"
                       class="custom-control-label"
                       for="myChemicalsToggle"
                       :style="myChemicalsCheckboxCursorStyle"
                >{{ $t("myChemicals") }}</label>
            </div>
            <Button icon="pi pi-download"
                    v-tooltip.top="'Download list of my chemicals'"
                    v-if="authorized"
                    :disabled="!clientChemicalsOnly"
                    @click="this.chemicalService.downloadRegisteredChemicals"/>
        </nav>

        <main class="row">
            <div v-if="error" class="col">{{ $t("somethingWentWrong") }}</div>
            <table v-else class="col table table-hover">
                <thead>
                <tr>
                    <th class="h3 border-0">
                        <span :style="{ color: tableFontColor }">
                            {{ $t(tableHeaderInternationalizationKey) }}
                        </span>
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="chemicals.length === 0 && !firstLoad">
                    <td>{{ $t("noChemicalsFound") }}</td>
                </tr>
                <tr v-else v-for="chemical in chemicals" :key="chemical.id" style="cursor: pointer">
                    <router-link
                            tag="td"
                            scope="row"
                            :to="{ path: `/chemicals/${chemical.id}` }"
                            :style="{ color: tableFontColor }"
                    >{{ chemical.name }}
                    </router-link>
                </tr>
                </tbody>
            </table>
            <div v-if="loading" class="col d-flex justify-content-center position-absolute" style="left: 0">
                <div
                        class="spinner-border mt-4"
                        role="status"
                        style="width: 10rem; height: 10rem;"
                ></div>
            </div>
        </main>

    </article>
</template>

<script>
    import debounce from "debounce";
    import urljoin from 'url-join';
    import url from "url";
    import ChemicalService from "@/service/ChemicalService";
    import UserSession from "@/service/UserSession";

    export default {
        metaInfo() {
            return {
                title: this.$t("searchChemicalsTitle"),
                htmlAttrs: {
                    lang: this.$i18n.locale,
                    amp: true
                }
            };
        },
        chemicalService: null,
        data() {
            return {
                loading: true,
                chemicals: [],
                clientChemicalsOnly: true,
                error: null,
                firstLoad: true,
                search: "",
                tableHeaderInternationalizationKey: "recentlyUpdated"
            };
        },
        created() {
            this.userSession = new UserSession(localStorage);
            this.chemicalService = new ChemicalService();
            this.fetchChemicals = debounce(this.fetchChemicals, 500);
        },
        mounted() {
            if (this.$route.query.mine && !this.authorized) {
                this.$router.push(this.logInRedirectUrl);
                return;
            }

            this.clientChemicalsOnly = this.authorized && !this.$route.query.all;
            this.search = this.$route.query.q;

            this.fetchChemicals(this.search, this.clientChemicalsOnly);
        },
        methods: {
            onChange() {
                this.fetchChemicals(this.search, this.clientChemicalsOnly);
            },
            fetchChemicals(searchString, clientChemicalsOnly) {
                this.loading = true;
                this.error = false;

                this.chemicalService.find(searchString, clientChemicalsOnly)
                    .then(response => {
                        const search = url.parse(response.config.url).search || '';

                        this.$router.replace(urljoin(this.$route.path, search));

                        this.firstLoad = false;
                        this.chemicals = response.data;
                        this.loading = false;
                        this.tableHeaderInternationalizationKey = search ? "searchResults" : "recentlyUpdated";
                    }, () => this.error = true);
            }
        },
        computed: {
            authorized() {
                return this.userSession.accessToken;
            },
            logInRedirectUrl() {

                if (this.authorized) {
                    return '/logout';
                }

                return `/login?continue=${encodeURIComponent(this.$route.fullPath)}`;
            },
            myChemicalsCheckboxTooltip() {

                if (this.authorized) {
                    return this.$t('myChemicalsTooltip');
                }

                return this.$t('logInFirst');
            },
            myChemicalsCheckboxCursorStyle() {
                let style = {
                    "user-select": "none"
                };

                if (this.authorized) {
                    style.cursor = "pointer";
                } else {
                    style.cursor = "not-allowed";
                }

                return style;
            },
            tableFontColor() {
                if (this.loading) {
                    return "gray";
                }
                return "black";
            }
        },
        watch: {
            clientChemicalsOnly: function (newToggleValue) {
                this.fetchChemicals(this.search, newToggleValue);
            }
        }
    };
</script>

<style lang="scss">
    @import '~bootstrap/scss/bootstrap';

    .container { /* due to IE 11 */
      margin-left: 0;
      margin-right: 0;
      justify-content: center;
    }
</style>

<i18n lang="yaml" locale="en">
    searchChemicalsTitle: "Chemical Information"
    myChemicalsTooltip: "Filter only chemicals you have associated to your account"
    myChemicals: "My chemicals"
    noChemicalsFound: "No chemicals found!"
    chemicalNameOrPartNumber: "Chemical name or item no."
    recentlyUpdated: "Recently Updated"
    searchResults: "Search Results"
</i18n>

<i18n lang="yaml" locale="dk">
    searchChemicalsTitle: "Kemikalie information"
    myChemicalsTooltip: "Kemikalier tilmeldt mnavigator"
    myChemicals: "Mine kemikalier"
    noChemicalsFound: "Intet kemikalie fundet!"
    chemicalNameOrPartNumber: "Kemikalie navn eller part nr."
    recentlyUpdated: "Nyeste Opdateret"
    searchResults: "Søgeresultater"
</i18n>
